import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

// ─── Types ───────────────────────────────────────────────────────────

export type ProductState = "billed" | "contracted" | "none";

export interface WhitespaceGroup {
  key: string;
  label: string;
  color: string;
}

export interface WhitespaceCustomer {
  qb_id: number;
  name: string | null;
  since_year: number | null;
  platform: boolean;
  arr: number;
  tier: number | null;
  csm_token: string | null;
  csm_label: string | null;
  products: Record<string, ProductState>;
}

export interface ContractCacheInfo {
  last_synced_at: string | null;
  status: string | null;
  detail: string | null;
}

export interface WhitespaceResponse {
  month: string;
  refreshed_at: string;
  contract_cache: ContractCacheInfo;
  groups: WhitespaceGroup[];
  customers: WhitespaceCustomer[];
}

export interface CsmFilterOption {
  value: string;
  label: string;
  kind: "owner" | "user" | "partner";
  account_count: number;
}

export interface GroupItem {
  cinch_item_id: string | null;
  item_name: string;
}

export interface ProductGroupConfig {
  id: string;
  key: string;
  label: string;
  color: string;
  sort_order: number;
  is_active: boolean;
  updated_by_name: string | null;
  updated_at: string | null;
  items: GroupItem[];
}

export interface GroupWritePayload {
  label?: string;
  key?: string;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
  items?: GroupItem[];
}

export interface BillingItemRow {
  id: string;
  name: string | null;
  category: string | null;
  is_archived?: boolean | null;
}

export interface PackageRow {
  id: string;
  name: string | null;
  package_items?: {
    item: string | null;
    item_detail?: { name?: string | null } | null;
  }[];
}

// ─── Hooks ───────────────────────────────────────────────────────────

const KEY = ["upsell-ops"] as const;

export function useWhitespace(month?: string | null) {
  return useQuery({
    queryKey: [...KEY, "whitespace", month ?? "latest"],
    queryFn: () =>
      apiFetch<WhitespaceResponse>(
        `/upsell-ops/whitespace${month ? `?month=${encodeURIComponent(month)}` : ""}`,
      ),
  });
}

export function useUpsellCsmOptions() {
  return useQuery({
    queryKey: [...KEY, "csm-options"],
    queryFn: () => apiFetch<{ options: CsmFilterOption[] }>("/upsell-ops/csm-options"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useGroups() {
  return useQuery({
    queryKey: [...KEY, "groups"],
    queryFn: () => apiFetch<{ groups: ProductGroupConfig[] }>("/upsell-ops/groups"),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: GroupWritePayload) =>
      apiFetch<ProductGroupConfig>("/upsell-ops/groups", { method: "POST", body: payload }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: GroupWritePayload }) =>
      apiFetch<ProductGroupConfig>(`/upsell-ops/groups/${id}`, {
        method: "PATCH",
        body: payload,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/upsell-ops/groups/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Live Cinch billing-item catalog for the group editor (admin only). */
export function useBillingItems(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "billing-items"],
    queryFn: async () => {
      const data = await apiFetch<{ results?: BillingItemRow[] } | BillingItemRow[]>(
        "/upsell-ops/billing-items",
      );
      return Array.isArray(data) ? data : (data.results ?? []);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Live Cinch packages — the editor's "add a package" shortcut expands a
 *  package into its component items before save. */
export function usePackages(enabled: boolean) {
  return useQuery({
    queryKey: [...KEY, "packages"],
    queryFn: async () => {
      const data = await apiFetch<{ results?: PackageRow[] } | PackageRow[]>(
        "/upsell-ops/packages",
      );
      return Array.isArray(data) ? data : (data.results ?? []);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRefreshContracts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ rows: number; status: string; last_synced_at: string }>(
        "/upsell-ops/refresh-contracts",
        { method: "POST" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
